import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import { ShieldCheck, CheckCircle2, XCircle, ChevronDown, Loader2 } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${m}/${day}/${y}`;
};

function ReviewRow({ cred, onSaved }) {
  const [open, setOpen] = useState(false);
  const [expires, setExpires] = useState(cred.expires);
  const [notes, setNotes] = useState(cred.verification_notes || "");
  const [source, setSource] = useState(cred.verification_source || "");
  const [busy, setBusy] = useState(false);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      await api.patch(`/admin/credentials/${cred.id}`, body);
      toast.success(msg);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`admin-row-${cred.id}`} className="border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="h-10 w-10 shrink-0 overflow-hidden border border-slate-200 bg-slate-100">
            {cred.worker.photo_url ? (
              <img src={cred.worker.photo_url} alt={cred.worker.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-[10px] font-black text-slate-400">
                {cred.worker.name.split(" ").map((n) => n[0]).join("")}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm font-bold text-[#0B1221]">{cred.name}</div>
            <div className="text-[11px] text-slate-500">
              {cred.worker.name} · {cred.worker.trade} · Issuer: {cred.issuer}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={cred.status} />
          <button
            type="button"
            data-testid={`admin-toggle-${cred.id}`}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#0B1221] hover:border-[#0B1221]"
          >
            {open ? "Close" : "Review"}
            <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs md:grid-cols-3">
            {[
              ["Type", cred.credential_type],
              ["Trade", cred.trade],
              ["License #", cred.license_number],
              ["State", cred.state_jurisdiction],
              ["Issued", fmtDate(cred.issue_date)],
              ["Doc", cred.supporting_document],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{k}</dt>
                <dd className="text-[12px] text-[#0B1221]">{v || <span className="text-slate-400">—</span>}</dd>
              </div>
            ))}
          </dl>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Expiration date</label>
              <input
                data-testid={`admin-expires-${cred.id}`}
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Verification source</label>
              <input
                data-testid={`admin-source-${cred.id}`}
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g. State L&I registry cross-check"
                className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">Verification notes</label>
              <input
                data-testid={`admin-notes-${cred.id}`}
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal note"
                className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid={`admin-approve-${cred.id}`}
              disabled={busy}
              onClick={() => patch(
                { verification: "ProCred Verified", expires, verification_source: source || null, verification_notes: notes || null },
                "Credential approved"
              )}
              className="inline-flex items-center gap-1.5 bg-emerald-600 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
              Approve
            </button>
            <button
              type="button"
              data-testid={`admin-reject-${cred.id}`}
              disabled={busy}
              onClick={() => patch(
                { verification: "Rejected", verification_notes: notes || null },
                "Credential rejected"
              )}
              className="inline-flex items-center gap-1.5 bg-red-600 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-60"
            >
              <XCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
              Reject
            </button>
            <button
              type="button"
              data-testid={`admin-save-${cred.id}`}
              disabled={busy}
              onClick={() => patch(
                { expires, verification_source: source || null, verification_notes: notes || null },
                "Credential updated"
              )}
              className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#0B1221] hover:border-[#0B1221] disabled:opacity-60"
            >
              Save changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPortal() {
  const [rows, setRows] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(true);

  const load = () => {
    setRows(null);
    api.get(`/admin/credentials?pending_only=${pendingOnly}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  };

  useEffect(load, [pendingOnly]);

  return (
    <div data-testid="admin-portal-page" className="min-h-[calc(100vh-65px)] bg-[#F8FAFC]">
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-12 md:py-14">
        <div className="flex flex-col items-start justify-between gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-end">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
              04 / Admin · Verification Console
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0B1221] sm:text-4xl">
              Credential Verification
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Approve, reject, or amend credentials submitted by workers. Actions immediately update the Worker Wallet, QR Verify, and Employer Dashboard views.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {[
              { key: true, label: "Pending" },
              { key: false, label: "All" },
            ].map((f) => (
              <button
                key={String(f.key)}
                type="button"
                data-testid={`admin-filter-${f.label.toLowerCase()}`}
                onClick={() => setPendingOnly(f.key)}
                className={`border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                  pendingOnly === f.key
                    ? "border-[#0B1221] bg-[#0B1221] text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {rows === null && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading credentials…
            </div>
          )}
          {rows && rows.length === 0 && (
            <div className="border border-slate-200 bg-white p-10 text-center">
              <ShieldCheck className="mx-auto h-8 w-8 text-emerald-500" strokeWidth={1.75} />
              <div className="font-display mt-3 text-lg font-bold text-[#0B1221]">No credentials to review</div>
              <div className="mt-1 text-sm text-slate-500">You&apos;re all caught up.</div>
            </div>
          )}
          {rows && rows.length > 0 && (
            <div className="space-y-3" data-testid="admin-rows">
              {rows.map((cred) => (
                <ReviewRow key={cred.id} cred={cred} onSaved={load} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
